// ========================================
// PÁGINA DE CONFIGURAÇÕES
// frontend/src/components/Configuracoes.jsx
// ========================================

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Settings, Users, Table2, Package, RefreshCw, Search, Filter, 
  Edit3, X, Check, AlertCircle, ChevronLeft, ChevronRight,
  Plus, ToggleLeft, ToggleRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import GerenciarUsuarios from './GerenciarUsuarios';
import Modal from './Modal';
import Loading from './Loading';
import { api } from '../config/api';

// ========================================
// COMPONENTE: CARD ANIMADO
// ========================================
const CardAnimado = ({ children, className = '' }) => (
  <div className={`transform transition-all duration-300 hover:shadow-xl ${className}`}>
    {children}
  </div>
);

// ========================================
// COMPONENTE: LINHA EDITÁVEL - CUSTO PRODUTO
// ========================================
const LinhaCustoProduto = ({ produto, onSave, onCancel, onToggleAtivo, isEditing, onStartEdit, isNew = false }) => {
  const [formData, setFormData] = useState({
    estado: produto.estado || '',
    grupo_produto: produto.grupo_produto || '',
    familia_produto: produto.familia_produto || '',
    descricao: produto.descricao || '',
    tipo_tabela: produto.tipo_tabela || '',
    custo_producao: produto.custo_producao || 0,
    margem_exigida: produto.margem_exigida || 0,
    valor_minimo_venda: produto.valor_minimo_venda || 0,
    valor_sugerido_venda: produto.valor_sugerido_venda || 0,
  });
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    setFormData({
      estado: produto.estado || '',
      grupo_produto: produto.grupo_produto || '',
      familia_produto: produto.familia_produto || '',
      descricao: produto.descricao || '',
      tipo_tabela: produto.tipo_tabela || '',
      custo_producao: produto.custo_producao || 0,
      margem_exigida: produto.margem_exigida || 0,
      valor_minimo_venda: produto.valor_minimo_venda || 0,
      valor_sugerido_venda: produto.valor_sugerido_venda || 0,
    });
  }, [produto]);

  const handleChange = (campo, valor) => {
    setFormData(prev => ({
      ...prev,
      [campo]: ['custo_producao', 'margem_exigida', 'valor_minimo_venda', 'valor_sugerido_venda'].includes(campo)
        ? (valor === '' ? '' : parseFloat(valor) || 0)
        : valor
    }));
  };

  const handleSave = async () => {
    setSalvando(true);
    try {
      await onSave(produto.id, {
        ...formData,
        custo_producao: parseFloat(formData.custo_producao) || 0,
        margem_exigida: parseFloat(formData.margem_exigida) || 0,
        valor_minimo_venda: parseFloat(formData.valor_minimo_venda) || 0,
        valor_sugerido_venda: parseFloat(formData.valor_sugerido_venda) || 0,
      }, isNew);
    } finally {
      setSalvando(false);
    }
  };

  const formatarMoeda = (valor) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
  const formatarPorcentagem = (valor) => `${(valor || 0).toFixed(2)}%`;

  const isAtivo = produto.ativo !== false;

  return (
    <tr className={`border-b transition-all duration-300 hover:bg-blue-50 hover:shadow-md ${isEditing ? 'bg-amber-50 shadow-inner' : ''} ${!isAtivo ? 'opacity-50 bg-gray-100' : ''}`}>
      {/* Estado */}
      <td className="py-2 px-2">
        {isEditing ? (
          <select value={formData.estado} onChange={(e) => handleChange('estado', e.target.value)}
            className="w-28 px-2 py-1 border-2 border-amber-400 rounded text-xs font-bold focus:outline-none focus:border-blue-500 transition-all bg-white cursor-pointer">
            <option value="">Selecione...</option>
            <option value="Parana">Paraná</option>
            <option value="Santa Catarina">Santa Catarina</option>
          </select>
        ) : (
          <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">{produto.estado}</span>
        )}
      </td>
      
      {/* Grupo */}
      <td className="py-2 px-2">
        {isEditing ? (
          <input type="text" value={formData.grupo_produto} onChange={(e) => handleChange('grupo_produto', e.target.value)}
            className="w-20 px-2 py-1 border-2 border-amber-400 rounded text-xs focus:outline-none focus:border-blue-500 transition-all" />
        ) : (
          <span className="text-sm font-medium text-gray-700">{produto.grupo_produto}</span>
        )}
      </td>
      
      {/* Família */}
      <td className="py-2 px-2">
        {isEditing ? (
          <input type="text" value={formData.familia_produto} onChange={(e) => handleChange('familia_produto', e.target.value)}
            className="w-24 px-2 py-1 border-2 border-amber-400 rounded text-xs focus:outline-none focus:border-blue-500 transition-all" />
        ) : (
          <span className="text-sm text-gray-600">{produto.familia_produto}</span>
        )}
      </td>
      
      {/* Descrição */}
      <td className="py-2 px-2">
        {isEditing ? (
          <input type="text" value={formData.descricao} onChange={(e) => handleChange('descricao', e.target.value)}
            className="w-40 px-2 py-1 border-2 border-amber-400 rounded text-xs focus:outline-none focus:border-blue-500 transition-all" />
        ) : (
          <span className="text-sm font-semibold text-gray-800">{produto.descricao}</span>
        )}
      </td>
      
      {/* Tabela */}
      <td className="py-2 px-2 text-center">
        {isEditing ? (
          <select value={formData.tipo_tabela} onChange={(e) => handleChange('tipo_tabela', e.target.value)}
            className="w-24 px-2 py-1 border-2 border-amber-400 rounded text-xs text-center focus:outline-none focus:border-blue-500 transition-all bg-white cursor-pointer">
            <option value="">Selecione...</option>
            <option value="Tabela 1">Tabela 1</option>
            <option value="Tabela 2">Tabela 2</option>
          </select>
        ) : (
          <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs">{produto.tipo_tabela}</span>
        )}
      </td>
      
      {/* Custo Produção */}
      <td className="py-2 px-2 text-center">
        {isEditing ? (
          <input type="number" step="0.01" min="0" value={formData.custo_producao} onChange={(e) => handleChange('custo_producao', e.target.value)}
            className="w-20 px-2 py-1 border-2 border-amber-400 rounded text-xs text-center focus:outline-none focus:border-blue-500 transition-all" />
        ) : (
          <span className="text-sm font-semibold text-emerald-700">{formatarMoeda(produto.custo_producao)}</span>
        )}
      </td>
      
      {/* Margem */}
      <td className="py-2 px-2 text-center">
        {isEditing ? (
          <input type="number" step="0.01" min="0" max="100" value={formData.margem_exigida} onChange={(e) => handleChange('margem_exigida', e.target.value)}
            className="w-16 px-2 py-1 border-2 border-amber-400 rounded text-xs text-center focus:outline-none focus:border-blue-500 transition-all" />
        ) : (
          <span className="text-sm font-medium text-gray-600">{formatarPorcentagem(produto.margem_exigida)}</span>
        )}
      </td>

      {/* Valor Mínimo */}
      <td className="py-2 px-2 text-center">
        {isEditing ? (
          <input type="number" step="0.01" min="0" value={formData.valor_minimo_venda} onChange={(e) => handleChange('valor_minimo_venda', e.target.value)}
            className="w-20 px-2 py-1 border-2 border-amber-400 rounded text-xs text-center focus:outline-none focus:border-blue-500 transition-all" />
        ) : (
          <span className="text-sm font-semibold text-orange-600">{formatarMoeda(produto.valor_minimo_venda)}</span>
        )}
      </td>

      {/* Valor Sugerido */}
      <td className="py-2 px-2 text-center">
        {isEditing ? (
          <input type="number" step="0.01" min="0" value={formData.valor_sugerido_venda} onChange={(e) => handleChange('valor_sugerido_venda', e.target.value)}
            className="w-20 px-2 py-1 border-2 border-amber-400 rounded text-xs text-center focus:outline-none focus:border-blue-500 transition-all" />
        ) : (
          <span className="text-sm font-semibold text-blue-600">{formatarMoeda(produto.valor_sugerido_venda)}</span>
        )}
      </td>

      {/* Status */}
      <td className="py-2 px-2 text-center">
        <span className={`px-2 py-1 rounded-full text-xs font-bold transition-all ${isAtivo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {isAtivo ? '✓ Ativo' : '✗ Inativo'}
        </span>
      </td>

      {/* Última Alteração */}
      <td className="py-2 px-2 text-center">
        {produto.updated_at ? (
          <div className="text-xs">
            <div className="font-semibold text-gray-700">
              {new Date(produto.updated_at).toLocaleDateString('pt-BR')}, {new Date(produto.updated_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </div>
            {produto.alterado_por && (
              <div className="text-gray-500 text-[10px]">por {produto.alterado_por}</div>
            )}
          </div>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </td>

      {/* Ações */}
      <td className="py-2 px-2">
        <div className="flex items-center justify-center gap-1">
          {isEditing ? (
            <>
              <button onClick={handleSave} disabled={salvando}
                className="p-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 transition-all transform hover:scale-110 disabled:opacity-50" title="Salvar">
                {salvando ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
              </button>
              <button onClick={onCancel} disabled={salvando}
                className="p-1.5 rounded bg-gray-500 text-white hover:bg-gray-600 transition-all transform hover:scale-110" title="Cancelar">
                <X size={14} />
              </button>
            </>
          ) : (
            <>
              <button onClick={() => onStartEdit(produto.id)}
                className="p-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 transition-all transform hover:scale-110" title="Editar">
                <Edit3 size={14} />
              </button>
              <button onClick={() => onToggleAtivo(produto.id, !isAtivo)}
                className={`p-1.5 rounded transition-all transform hover:scale-110 ${isAtivo ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'} text-white`}
                title={isAtivo ? 'Desativar' : 'Ativar'}>
                {isAtivo ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

// ========================================
// COMPONENTE: LINHA EDITÁVEL - PRODUTOS
// ========================================
const LinhaProduto = ({ produto, onSave, onCancel, onToggleAtivo, isEditing, onStartEdit, isNew = false, gruposDisponiveis = [], familiasDisponiveis = [] }) => {
  const [formData, setFormData] = useState({
    codigo: produto.codigo || '',
    grupo_produto: produto.grupo_produto || '',
    familia_produto: produto.familia_produto || '',
    produto: produto.produto || '',
    tipo: produto.tipo || '',
  });
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    setFormData({
      codigo: produto.codigo || '',
      grupo_produto: produto.grupo_produto || '',
      familia_produto: produto.familia_produto || '',
      produto: produto.produto || '',
      tipo: produto.tipo || '',
    });
  }, [produto]);

  const handleChange = (campo, valor) => {
    setFormData(prev => ({ ...prev, [campo]: valor }));
  };

  const handleSave = async () => {
    setSalvando(true);
    try {
      await onSave(produto.id, formData, isNew);
    } finally {
      setSalvando(false);
    }
  };

  const isAtivo = produto.ativo !== false;

  const formatarData = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Filtrar famílias baseado no grupo selecionado
  const familiasFiltradas = formData.grupo_produto 
    ? familiasDisponiveis.filter(f => f.grupo === formData.grupo_produto)
    : familiasDisponiveis;

  return (
    <tr className={`border-b transition-all duration-300 hover:bg-blue-50 hover:shadow-md ${isEditing ? 'bg-amber-50 shadow-inner' : ''} ${!isAtivo ? 'opacity-50 bg-gray-100' : ''}`}>
      {/* Código */}
      <td className="py-2 px-2 text-center">
        {isEditing ? (
          <input type="text" value={formData.codigo} readOnly
            className="w-16 px-2 py-1 border-2 rounded text-xs text-center border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed" placeholder="Cód" />
        ) : (
          <span className="text-xs font-mono text-gray-600">{produto.codigo || '-'}</span>
        )}
      </td>
      
      {/* Grupo */}
      <td className="py-2 px-2">
        {isEditing ? (
          <select value={formData.grupo_produto} onChange={(e) => { handleChange('grupo_produto', e.target.value); handleChange('familia_produto', ''); }}
            className="w-28 px-2 py-1 border-2 border-amber-400 rounded text-xs focus:outline-none focus:border-blue-500 transition-all bg-white cursor-pointer">
            <option value="">Selecione...</option>
            {gruposDisponiveis.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        ) : (
          <span className="text-sm font-medium text-gray-700">{produto.grupo_produto || '-'}</span>
        )}
      </td>
      
      {/* Família */}
      <td className="py-2 px-2">
        {isEditing ? (
          <select value={formData.familia_produto} onChange={(e) => handleChange('familia_produto', e.target.value)}
            className="w-32 px-2 py-1 border-2 border-amber-400 rounded text-xs focus:outline-none focus:border-blue-500 transition-all bg-white cursor-pointer"
            disabled={!formData.grupo_produto}>
            <option value="">{formData.grupo_produto ? 'Selecione...' : 'Selecione grupo primeiro'}</option>
            {familiasFiltradas.map(f => <option key={f.familia} value={f.familia}>{f.familia}</option>)}
          </select>
        ) : (
          <span className="text-sm text-gray-600">{produto.familia_produto || '-'}</span>
        )}
      </td>
      
      {/* Produto */}
      <td className="py-2 px-2">
        {isEditing ? (
          <input type="text" value={formData.produto} onChange={(e) => handleChange('produto', e.target.value)}
            className="w-full px-2 py-1 border-2 border-amber-400 rounded text-xs focus:outline-none focus:border-blue-500 transition-all" placeholder="Nome do produto" />
        ) : (
          <span className="text-sm font-semibold text-gray-800">{produto.produto}</span>
        )}
      </td>
      
      {/* Tipo - Texto livre */}
      <td className="py-2 px-2 text-center">
        {isEditing ? (
          <input type="text" value={formData.tipo} onChange={(e) => handleChange('tipo', e.target.value.toUpperCase())}
            className="w-16 px-2 py-1 border-2 border-amber-400 rounded text-xs text-center focus:outline-none focus:border-blue-500 transition-all" placeholder="Tipo" />
        ) : (
          <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs font-semibold">
            {produto.tipo || '-'}
          </span>
        )}
      </td>
      
      {/* Status */}
      <td className="py-2 px-2 text-center">
        <span className={`px-2 py-1 rounded-full text-xs font-bold transition-all ${isAtivo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {isAtivo ? '✓ Ativo' : '✗ Inativo'}
        </span>
      </td>
      
      {/* Última Alteração */}
      <td className="py-2 px-2 text-center">
        <div className="text-xs text-gray-500">
          <div>{formatarData(produto.updated_at)}</div>
          {produto.alterado_por && <div className="text-[10px] text-gray-400">por {produto.alterado_por}</div>}
        </div>
      </td>

      {/* Ações */}
      <td className="py-2 px-2">
        <div className="flex items-center justify-center gap-1">
          {isEditing ? (
            <>
              <button onClick={handleSave} disabled={salvando}
                className="p-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 transition-all transform hover:scale-110 disabled:opacity-50" title="Salvar">
                {salvando ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
              </button>
              <button onClick={onCancel} disabled={salvando}
                className="p-1.5 rounded bg-gray-500 text-white hover:bg-gray-600 transition-all transform hover:scale-110" title="Cancelar">
                <X size={14} />
              </button>
            </>
          ) : (
            <>
              <button onClick={() => onStartEdit(produto.id)}
                className="p-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 transition-all transform hover:scale-110" title="Editar">
                <Edit3 size={14} />
              </button>
              <button onClick={() => onToggleAtivo(produto.id, !isAtivo)}
                className={`p-1.5 rounded transition-all transform hover:scale-110 ${isAtivo ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'} text-white`}
                title={isAtivo ? 'Desativar' : 'Ativar'}>
                {isAtivo ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

// ========================================
// COMPONENTE: GERENCIAR TABELA CUSTO PRODUTO
// ========================================
const TabelaCustoProduto = () => {
  const { usuario } = useAuth();
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [linhaEditando, setLinhaEditando] = useState(null);
  const [novaLinha, setNovaLinha] = useState(null);
  const [modalInfo, setModalInfo] = useState({ open: false, type: 'info', title: '', message: '' });
  const [filtros, setFiltros] = useState({ busca: '', estado: '', grupo: '', familia: '', ocultarInativos: false });
  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 12;

  const carregarProdutos = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const response = await api.get('/tabelas/custo-produto');
      if (!response.success) throw new Error(response.message);
      setProdutos(response.produtos || []);
    } catch (error) {
      setErro(error.message || 'Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregarProdutos(); }, [carregarProdutos]);

  const handleSalvar = async (produtoId, dados, isNew = false) => {
    try {
      if (isNew) {
        const response = await api.post('/tabelas/custo-produto', dados);
        if (!response.success) throw new Error(response.message);
        setProdutos(prev => [...prev, response.produto]);
        setNovaLinha(null);
        setLinhaEditando(null);
      } else {
        const response = await api.put(`/tabelas/custo-produto/${produtoId}`, dados);
        if (!response.success) throw new Error(response.message);
        // Usar dados retornados do servidor (inclui updated_at e alterado_por)
        setProdutos(prev => prev.map(p => p.id === produtoId ? response.produto : p));
        setLinhaEditando(null);
      }
      setModalInfo({ open: true, type: 'success', title: 'Sucesso!', message: isNew ? 'Produto adicionado!' : 'Alteração salva!' });
    } catch (error) {
      setModalInfo({ open: true, type: 'error', title: 'Erro', message: error.message });
    }
  };

  const handleToggleAtivo = async (produtoId, novoStatus) => {
    try {
      const response = await api.patch(`/tabelas/custo-produto/${produtoId}/status`, { ativo: novoStatus });
      if (!response.success) throw new Error(response.message);
      // Usar dados retornados do servidor (inclui updated_at e alterado_por)
      setProdutos(prev => prev.map(p => p.id === produtoId ? response.produto : p));
      setModalInfo({ open: true, type: 'success', title: 'Sucesso!', message: `Produto ${novoStatus ? 'ativado' : 'desativado'}!` });
    } catch (error) {
      const mensagem = error.message.includes('Failed to fetch') 
        ? 'Erro de conexão. Verifique se o servidor está rodando.'
        : (error.message || 'Erro ao alterar status');
      setModalInfo({ open: true, type: 'error', title: 'Erro', message: mensagem });
    }
  };

  const adicionarNovaLinha = () => {
    setNovaLinha({ id: 'new', estado: '', grupo_produto: '', familia_produto: '', descricao: '', tipo_tabela: '', custo_producao: 0, margem_exigida: 0, valor_minimo_venda: 0, valor_sugerido_venda: 0, ativo: true });
    setLinhaEditando('new');
  };

  const produtosFiltrados = produtos.filter(p => {
    const matchBusca = !filtros.busca || p.descricao?.toLowerCase().includes(filtros.busca.toLowerCase()) || p.grupo_produto?.toLowerCase().includes(filtros.busca.toLowerCase());
    const matchEstado = !filtros.estado || p.estado === filtros.estado;
    const matchGrupo = !filtros.grupo || p.grupo_produto === filtros.grupo;
    const matchFamilia = !filtros.familia || p.familia_produto === filtros.familia;
    const matchAtivo = !filtros.ocultarInativos || p.ativo !== false;
    return matchBusca && matchEstado && matchGrupo && matchFamilia && matchAtivo;
  });

  const totalPaginas = Math.ceil(produtosFiltrados.length / itensPorPagina);
  const indiceInicial = (paginaAtual - 1) * itensPorPagina;
  const produtosPaginados = produtosFiltrados.slice(indiceInicial, indiceInicial + itensPorPagina);
  const estadosUnicos = [...new Set(produtos.map(p => p.estado).filter(Boolean))].sort();
  const gruposUnicos = [...new Set(produtos.map(p => p.grupo_produto).filter(Boolean))].sort();
  const familiasUnicas = [...new Set(produtos.map(p => p.familia_produto).filter(Boolean))].sort();

  if (loading) return <Loading texto="Carregando tabela..." tamanho="lg" fullScreen={false} />;

  return (
    <div className="space-y-4">
      {/* Header - Mesmo padrão de Gerenciar Usuários */}
      <div className="mb-6 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 group cursor-default">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
              <Table2 size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Custo de Produtos</h1>
              <p className="text-xs text-gray-400">Gerenciar custos e valores de venda</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-3xl font-bold text-blue-600">{produtosFiltrados.length}</p>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Produtos</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={adicionarNovaLinha} disabled={novaLinha}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:hover:scale-100">
                <Plus size={18} /> Adicionar
              </button>
              <button onClick={carregarProdutos} disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:shadow-md transition-all transform hover:scale-105">
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Atualizar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Linha divisória */}
      <div className="w-full h-px bg-gray-200 my-4"></div>

      {/* Filtros */}
      <CardAnimado className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={16} className="text-gray-500" />
          <span className="text-sm font-semibold text-gray-600">Filtros</span>
        </div>
        <div className="grid grid-cols-6 gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Buscar..." value={filtros.busca}
              onChange={(e) => { setFiltros(prev => ({ ...prev, busca: e.target.value })); setPaginaAtual(1); }}
              className="w-full pl-9 pr-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:outline-none transition-all hover:border-gray-300" />
          </div>
          <select value={filtros.estado} onChange={(e) => { setFiltros(prev => ({ ...prev, estado: e.target.value })); setPaginaAtual(1); }}
            className="border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none transition-all hover:border-gray-300 cursor-pointer">
            <option value="">Todos estados</option>
            {estadosUnicos.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
          <select value={filtros.grupo} onChange={(e) => { setFiltros(prev => ({ ...prev, grupo: e.target.value })); setPaginaAtual(1); }}
            className="border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none transition-all hover:border-gray-300 cursor-pointer">
            <option value="">Todos grupos</option>
            {gruposUnicos.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <select value={filtros.familia} onChange={(e) => { setFiltros(prev => ({ ...prev, familia: e.target.value })); setPaginaAtual(1); }}
            className="border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none transition-all hover:border-gray-300 cursor-pointer">
            <option value="">Todas famílias</option>
            {familiasUnicas.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded-lg px-3 py-2 transition-all">
            <input type="checkbox" checked={filtros.ocultarInativos} onChange={(e) => setFiltros(prev => ({ ...prev, ocultarInativos: e.target.checked }))}
              className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer" />
            <span className="text-sm text-gray-600">Ocultar inativos</span>
          </label>
        </div>
      </CardAnimado>

      {erro && (
        <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-center gap-3 animate-pulse">
          <AlertCircle size={20} className="text-red-600" />
          <p className="text-sm text-red-700">{erro}</p>
        </div>
      )}

      {/* Linha divisória */}
      <div className="w-full h-px bg-gray-200 my-4"></div>

      {/* Tabela */}
      <CardAnimado className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white">
              <tr>
                <th className="py-3 px-2 font-bold text-xs uppercase">Estado</th>
                <th className="py-3 px-2 font-bold text-xs uppercase">Grupo</th>
                <th className="py-3 px-2 font-bold text-xs uppercase">Família</th>
                <th className="py-3 px-2 font-bold text-xs uppercase">Descrição</th>
                <th className="py-3 px-2 font-bold text-xs uppercase">Tabela</th>
                <th className="py-3 px-2 font-bold text-xs uppercase">Custo</th>
                <th className="py-3 px-2 font-bold text-xs uppercase">Margem</th>
                <th className="py-3 px-2 font-bold text-xs uppercase">Vlr. Mín.</th>
                <th className="py-3 px-2 font-bold text-xs uppercase">Vlr. Sug.</th>
                <th className="py-3 px-2 font-bold text-xs uppercase">Status</th>
                <th className="py-3 px-2 font-bold text-xs uppercase">Últ. Alteração</th>
                <th className="py-3 px-2 font-bold text-xs uppercase">Ações</th>
              </tr>
            </thead>
            <tbody>
              {novaLinha && (
                <LinhaCustoProduto produto={novaLinha} isEditing={true} isNew={true}
                  onSave={handleSalvar} onCancel={() => { setNovaLinha(null); setLinhaEditando(null); }}
                  onToggleAtivo={() => {}} onStartEdit={() => {}} />
              )}
              {produtosPaginados.length === 0 && !novaLinha ? (
                <tr><td colSpan={12} className="py-12 text-center text-gray-500">
                  <Table2 size={48} className="mx-auto mb-3 text-gray-300" />
                  <p className="font-semibold">Nenhum produto encontrado</p>
                </td></tr>
              ) : (
                produtosPaginados.map(produto => (
                  <LinhaCustoProduto key={produto.id} produto={produto}
                    isEditing={linhaEditando === produto.id}
                    onStartEdit={(id) => setLinhaEditando(id)}
                    onCancel={() => setLinhaEditando(null)}
                    onSave={handleSalvar}
                    onToggleAtivo={handleToggleAtivo} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardAnimado>

      {/* Linha divisória */}
      <div className="w-full h-px bg-gray-200 my-4"></div>

      {/* Paginação */}
      {totalPaginas > 1 && (
        <div className="flex items-center justify-between bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <span className="text-sm text-gray-600">Mostrando {indiceInicial + 1} a {Math.min(indiceInicial + itensPorPagina, produtosFiltrados.length)} de {produtosFiltrados.length}</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setPaginaAtual(prev => Math.max(1, prev - 1))} disabled={paginaAtual === 1}
              className="p-2 rounded-lg border-2 border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition-all transform hover:scale-105 disabled:hover:scale-100">
              <ChevronLeft size={18} />
            </button>
            {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
              let pageNum = totalPaginas <= 5 ? i + 1 : paginaAtual <= 3 ? i + 1 : paginaAtual >= totalPaginas - 2 ? totalPaginas - 4 + i : paginaAtual - 2 + i;
              return (
                <button key={pageNum} onClick={() => setPaginaAtual(pageNum)}
                  className={`w-10 h-10 rounded-lg text-sm font-semibold transition-all transform hover:scale-105 ${paginaAtual === pageNum ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg' : 'border-2 border-gray-200 hover:bg-gray-50'}`}>
                  {pageNum}
                </button>
              );
            })}
            <button onClick={() => setPaginaAtual(prev => Math.min(totalPaginas, prev + 1))} disabled={paginaAtual === totalPaginas}
              className="p-2 rounded-lg border-2 border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition-all transform hover:scale-105 disabled:hover:scale-100">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Linha divisória final */}
      <div className="w-full h-px bg-gray-200 mt-6 mb-8"></div>

      <Modal open={modalInfo.open} type={modalInfo.type} title={modalInfo.title} message={modalInfo.message} onClose={() => setModalInfo({ ...modalInfo, open: false })} />
    </div>
  );
};

// ========================================
// COMPONENTE: GERENCIAR TABELA PRODUTOS
// ========================================
const TabelaProdutos = () => {
  const { usuario } = useAuth();
  const [produtos, setProdutos] = useState([]);
  const [custosProdutos, setCustosProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [linhaEditando, setLinhaEditando] = useState(null);
  const [novaLinha, setNovaLinha] = useState(null);
  const [modalInfo, setModalInfo] = useState({ open: false, type: 'info', title: '', message: '' });
  const [filtros, setFiltros] = useState({ busca: '', grupo: '', familia: '', tipo: '', ocultarInativos: false });
  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 15;

  const carregarProdutos = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      // Carregar produtos e custos em paralelo
      const [produtosRes, custosRes] = await Promise.all([
        api.get('/tabelas/produtos'),
        api.get('/tabelas/custo-produto')
      ]);
      if (!produtosRes.success) throw new Error(produtosRes.message);
      setProdutos(produtosRes.produtos || []);
      if (custosRes.success) {
        setCustosProdutos(custosRes.produtos || []);
      }
    } catch (error) {
      setErro(error.message || 'Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregarProdutos(); }, [carregarProdutos]);

  // Extrair grupos e famílias únicos combinando Custo de Produtos e Produtos cadastrados
  const gruposDisponiveis = [...new Set([
    ...custosProdutos.map(c => c.grupo_produto),
    ...produtos.map(p => p.grupo_produto)
  ].filter(Boolean))].sort();
  const familiasDisponiveis = [
    ...custosProdutos
      .filter(c => c.familia_produto)
      .map(c => ({ grupo: c.grupo_produto, familia: c.familia_produto })),
    ...produtos
      .filter(p => p.familia_produto)
      .map(p => ({ grupo: p.grupo_produto, familia: p.familia_produto }))
  ]
    .filter((item, index, self) => 
      index === self.findIndex(t => t.familia === item.familia && t.grupo === item.grupo)
    )
    .sort((a, b) => a.familia.localeCompare(b.familia));

  const handleSalvar = async (produtoId, dados, isNew = false) => {
    try {
      if (isNew) {
        const response = await api.post('/tabelas/produtos', dados);
        if (!response.success) throw new Error(response.message);
        setProdutos(prev => [...prev, response.produto]);
        setNovaLinha(null);
        setLinhaEditando(null);
      } else {
        const response = await api.put(`/tabelas/produtos/${produtoId}`, dados);
        if (!response.success) throw new Error(response.message);
        // Usar dados retornados do servidor (inclui updated_at e alterado_por)
        setProdutos(prev => prev.map(p => p.id === produtoId ? response.produto : p));
        setLinhaEditando(null);
      }
      setModalInfo({ open: true, type: 'success', title: 'Sucesso!', message: isNew ? 'Produto adicionado!' : 'Alteração salva!' });
    } catch (error) {
      setModalInfo({ open: true, type: 'error', title: 'Erro', message: error.message });
    }
  };

  const handleToggleAtivo = async (produtoId, novoStatus) => {
    try {
      const response = await api.patch(`/tabelas/produtos/${produtoId}/status`, { ativo: novoStatus });
      if (!response.success) throw new Error(response.message);
      // Usar dados retornados do servidor (inclui updated_at e alterado_por)
      setProdutos(prev => prev.map(p => p.id === produtoId ? response.produto : p));
      setModalInfo({ open: true, type: 'success', title: 'Sucesso!', message: `Produto ${novoStatus ? 'ativado' : 'desativado'}!` });
    } catch (error) {
      const mensagem = error.message.includes('Failed to fetch') 
        ? 'Erro de conexão. Verifique se o servidor está rodando.'
        : (error.message || 'Erro ao alterar status');
      setModalInfo({ open: true, type: 'error', title: 'Erro', message: mensagem });
    }
  };

  const adicionarNovaLinha = () => {
    // Calcular próximo código baseado no maior código existente
    const maiorCodigo = produtos.reduce((max, p) => {
      const codigo = parseInt(p.codigo) || 0;
      return codigo > max ? codigo : max;
    }, 0);
    const proximoCodigo = (maiorCodigo + 1).toString();
    
    setNovaLinha({ id: 'new', codigo: proximoCodigo, grupo_produto: '', familia_produto: '', produto: '', tipo: '', ativo: true });
    setLinhaEditando('new');
  };

  const produtosFiltrados = produtos.filter(p => {
    const matchBusca = !filtros.busca || p.produto?.toLowerCase().includes(filtros.busca.toLowerCase());
    const matchGrupo = !filtros.grupo || p.grupo_produto === filtros.grupo;
    const matchFamilia = !filtros.familia || p.familia_produto === filtros.familia;
    const matchTipo = !filtros.tipo || p.tipo === filtros.tipo;
    const matchAtivo = !filtros.ocultarInativos || p.ativo !== false;
    return matchBusca && matchGrupo && matchFamilia && matchTipo && matchAtivo;
  });

  const totalPaginas = Math.ceil(produtosFiltrados.length / itensPorPagina);
  const indiceInicial = (paginaAtual - 1) * itensPorPagina;
  const produtosPaginados = produtosFiltrados.slice(indiceInicial, indiceInicial + itensPorPagina);
  
  // Listas únicas para filtros
  const gruposUnicos = [...new Set(produtos.map(p => p.grupo_produto).filter(Boolean))].sort();
  const familiasUnicas = [...new Set(produtos.map(p => p.familia_produto).filter(Boolean))].sort();
  const tiposUnicos = [...new Set(produtos.map(p => p.tipo).filter(Boolean))].sort();

  if (loading) return <Loading texto="Carregando produtos..." tamanho="lg" fullScreen={false} />;

  return (
    <div className="space-y-4">
      {/* Header - Mesmo padrão de Gerenciar Usuários */}
      <div className="mb-6 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 group cursor-default">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
              <Package size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Cadastro de Produtos</h1>
              <p className="text-xs text-gray-400">Gerenciar produtos do sistema</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-3xl font-bold text-blue-600">{produtosFiltrados.length}</p>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Produtos</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={adicionarNovaLinha} disabled={novaLinha}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:hover:scale-100">
                <Plus size={18} /> Adicionar
              </button>
              <button onClick={carregarProdutos} disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:shadow-md transition-all transform hover:scale-105">
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Atualizar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Linha divisória */}
      <div className="w-full h-px bg-gray-200 my-4"></div>

      {/* Filtros */}
      <CardAnimado className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={16} className="text-gray-500" />
          <span className="text-sm font-semibold text-gray-600">Filtros</span>
        </div>
        <div className="grid grid-cols-6 gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Buscar produto..." value={filtros.busca}
              onChange={(e) => { setFiltros(prev => ({ ...prev, busca: e.target.value })); setPaginaAtual(1); }}
              className="w-full pl-9 pr-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:outline-none transition-all hover:border-gray-300" />
          </div>
          <select value={filtros.grupo} onChange={(e) => { setFiltros(prev => ({ ...prev, grupo: e.target.value })); setPaginaAtual(1); }}
            className="border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none transition-all hover:border-gray-300 cursor-pointer">
            <option value="">Todos grupos</option>
            {gruposUnicos.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <select value={filtros.familia} onChange={(e) => { setFiltros(prev => ({ ...prev, familia: e.target.value })); setPaginaAtual(1); }}
            className="border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none transition-all hover:border-gray-300 cursor-pointer">
            <option value="">Todas famílias</option>
            {familiasUnicas.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <select value={filtros.tipo} onChange={(e) => { setFiltros(prev => ({ ...prev, tipo: e.target.value })); setPaginaAtual(1); }}
            className="border-2 border-gray-200 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none transition-all hover:border-gray-300 cursor-pointer">
            <option value="">Todos tipos</option>
            {tiposUnicos.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <label className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded-lg px-3 py-2 transition-all">
            <input type="checkbox" checked={filtros.ocultarInativos} onChange={(e) => setFiltros(prev => ({ ...prev, ocultarInativos: e.target.checked }))}
              className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer" />
            <span className="text-sm text-gray-600">Ocultar inativos</span>
          </label>
        </div>
      </CardAnimado>

      {erro && (
        <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-center gap-3 animate-pulse">
          <AlertCircle size={20} className="text-red-600" />
          <p className="text-sm text-red-700">{erro}</p>
        </div>
      )}

      {/* Linha divisória */}
      <div className="w-full h-px bg-gray-200 my-4"></div>

      {/* Tabela */}
      <CardAnimado className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white">
              <tr>
                <th className="py-3 px-2 font-bold text-xs uppercase">Código</th>
                <th className="py-3 px-2 font-bold text-xs uppercase">Grupo</th>
                <th className="py-3 px-2 font-bold text-xs uppercase">Família</th>
                <th className="py-3 px-2 font-bold text-xs uppercase text-left">Produto</th>
                <th className="py-3 px-2 font-bold text-xs uppercase">Tipo</th>
                <th className="py-3 px-2 font-bold text-xs uppercase">Status</th>
                <th className="py-3 px-2 font-bold text-xs uppercase">Últ. Alteração</th>
                <th className="py-3 px-2 font-bold text-xs uppercase">Ações</th>
              </tr>
            </thead>
            <tbody>
              {novaLinha && (
                <LinhaProduto produto={novaLinha} isEditing={true} isNew={true}
                  onSave={handleSalvar} onCancel={() => { setNovaLinha(null); setLinhaEditando(null); }}
                  onToggleAtivo={() => {}} onStartEdit={() => {}}
                  gruposDisponiveis={gruposDisponiveis} familiasDisponiveis={familiasDisponiveis} />
              )}
              {produtosPaginados.length === 0 && !novaLinha ? (
                <tr><td colSpan={8} className="py-12 text-center text-gray-500">
                  <Package size={48} className="mx-auto mb-3 text-gray-300" />
                  <p className="font-semibold">Nenhum produto encontrado</p>
                </td></tr>
              ) : (
                produtosPaginados.map(produto => (
                  <LinhaProduto key={produto.id} produto={produto}
                    isEditing={linhaEditando === produto.id}
                    onStartEdit={(id) => setLinhaEditando(id)}
                    onCancel={() => setLinhaEditando(null)}
                    onSave={handleSalvar}
                    onToggleAtivo={handleToggleAtivo}
                    gruposDisponiveis={gruposDisponiveis} familiasDisponiveis={familiasDisponiveis} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardAnimado>

      {/* Linha divisória */}
      <div className="w-full h-px bg-gray-200 my-4"></div>

      {/* Paginação */}
      {totalPaginas > 1 && (
        <div className="flex items-center justify-between bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <span className="text-sm text-gray-600">Mostrando {indiceInicial + 1} a {Math.min(indiceInicial + itensPorPagina, produtosFiltrados.length)} de {produtosFiltrados.length}</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setPaginaAtual(prev => Math.max(1, prev - 1))} disabled={paginaAtual === 1}
              className="p-2 rounded-lg border-2 border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition-all transform hover:scale-105 disabled:hover:scale-100">
              <ChevronLeft size={18} />
            </button>
            {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
              let pageNum = totalPaginas <= 5 ? i + 1 : paginaAtual <= 3 ? i + 1 : paginaAtual >= totalPaginas - 2 ? totalPaginas - 4 + i : paginaAtual - 2 + i;
              return (
                <button key={pageNum} onClick={() => setPaginaAtual(pageNum)}
                  className={`w-10 h-10 rounded-lg text-sm font-semibold transition-all transform hover:scale-105 ${paginaAtual === pageNum ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg' : 'border-2 border-gray-200 hover:bg-gray-50'}`}>
                  {pageNum}
                </button>
              );
            })}
            <button onClick={() => setPaginaAtual(prev => Math.min(totalPaginas, prev + 1))} disabled={paginaAtual === totalPaginas}
              className="p-2 rounded-lg border-2 border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition-all transform hover:scale-105 disabled:hover:scale-100">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Linha divisória final */}
      <div className="w-full h-px bg-gray-200 mt-6 mb-8"></div>

      <Modal open={modalInfo.open} type={modalInfo.type} title={modalInfo.title} message={modalInfo.message} onClose={() => setModalInfo({ ...modalInfo, open: false })} />
    </div>
  );
};

// ========================================
// COMPONENTE PRINCIPAL: CONFIGURAÇÕES
// ========================================
const Configuracoes = ({ secaoInicial = 'usuarios' }) => {
  const { temPermissao, isAdminOuDiretoria } = useAuth();
  const [secaoAtiva, setSecaoAtiva] = useState(secaoInicial);
  const [subAbaTabelas, setSubAbaTabelas] = useState('custo_produto');

  // Atualiza seção quando prop muda
  useEffect(() => {
    setSecaoAtiva(secaoInicial);
  }, [secaoInicial]);

  const podeVerUsuarios = temPermissao('pode_aprovar_usuarios') || isAdminOuDiretoria();
  const podeVerTabelas = temPermissao('pode_gerenciar_tabelas') || isAdminOuDiretoria();

  if (!podeVerUsuarios && !podeVerTabelas) {
    return (
      <div className="min-h-screen p-6" style={{ backgroundColor: '#f3f4f6' }}>
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-8 text-center max-w-2xl mx-auto mt-10">
            <Settings size={64} className="mx-auto text-red-500 mb-4" />
            <h2 className="text-2xl font-bold text-red-800 mb-2">Acesso Negado</h2>
            <p className="text-red-700">Você não tem permissão para acessar as configurações.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#f3f4f6' }}>
      <div className="max-w-7xl mx-auto space-y-6">

      {/* CONTEÚDO */}
      {secaoAtiva === 'usuarios' && podeVerUsuarios && (
        <GerenciarUsuarios embedded={true} />
      )}
      
      {secaoAtiva === 'tabelas' && podeVerTabelas && (
        <div className="space-y-4">
          {/* Sub-abas para tabelas */}
          <div className="flex gap-2 bg-white p-2 rounded-xl shadow-sm border border-gray-200">
            <button onClick={() => setSubAbaTabelas('custo_produto')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 transform hover:scale-105 ${
                subAbaTabelas === 'custo_produto'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-md'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}>
              <Table2 size={16} />
              Custo de Produtos
            </button>
            <button onClick={() => setSubAbaTabelas('produtos')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 transform hover:scale-105 ${
                subAbaTabelas === 'produtos'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-md'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}>
              <Package size={16} />
              Cadastro de Produtos
            </button>
          </div>

          {subAbaTabelas === 'custo_produto' && <TabelaCustoProduto />}
          {subAbaTabelas === 'produtos' && <TabelaProdutos />}
        </div>
      )}
      </div>
    </div>
  );
};

export default Configuracoes;
